import Drawer from '@mui/material/Drawer';
import Fade from '@mui/material/Fade';
import type { Theme } from '@mui/material/styles';
import { usePageContentTop } from '@src/app/layout';
import {
  SaveShortcutBoundary,
  type SaveShortcutStrategy,
} from '@src/shared/ui/SaveShortcutScope';
import { Box, Stack } from '@src/shared/ui/layout';
import type { PropsWithChildren, ReactNode } from 'react';
import SidePanelContent from './SidePanelContent';

type SidePanelProps = PropsWithChildren<{
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  footer?: ReactNode;
  hideCloseButton?: boolean;
  disableBackdropClose?: boolean;
  shortcutBoundary?: boolean;
  shortcutStrategy?: SaveShortcutStrategy;
  /** A column left of the panel's content, e.g. suggestions for its form. */
  aside?: ReactNode;
  asideWidth?: number;
}>;

const DEFAULT_ASIDE_WIDTH = 476;

const StyleSheet = {
  paper: (contentTop: number, asideWidth: number) => (theme: Theme) => ({
    border: '1px solid',
    borderColor: 'grey.100',
    borderRadius: 2,
    overflow: 'hidden',
    // `position: fixed` (from the MUI Drawer paper) + top + bottom fully define
    // the height; `height: auto` just overrides the paper's default `height: 100%`.
    top: `${contentTop}px`,
    bottom: `${theme.layout.content.bottomPadding}px`,
    height: 'auto',
    // Align the panel's right edge with the page content's right padding.
    right: `${theme.layout.content.rightPadding}px`,
    width: theme.layout.aside.width + asideWidth,
  }),
  row: {
    height: '100%',
    minHeight: 0,
  },
  aside: (width: number) => ({
    borderRight: '1px solid',
    borderColor: 'grey.200',
    flexShrink: 0,
    width,
  }),
  // The rest of the paper: the panel's own width next to the aside.
  content: {
    flex: 1,
    minWidth: 0,
  },
};

function SidePanel({
  open,
  onClose,
  title,
  footer,
  hideCloseButton = false,
  disableBackdropClose = false,
  shortcutBoundary = true,
  shortcutStrategy = 'active',
  aside,
  asideWidth = DEFAULT_ASIDE_WIDTH,
  children,
}: SidePanelProps) {
  // Every SidePanel sits at the same height — right below the page header —
  // which the shared Page layout measures for us.
  const contentTop = usePageContentTop();

  const handleClose = (
    _event: object,
    reason: 'backdropClick' | 'escapeKeyDown',
  ) => {
    if (disableBackdropClose && reason === 'backdropClick') {
      return;
    }

    onClose();
  };

  const drawer = (
    <Drawer
      anchor="right"
      elevation={0}
      open={open}
      onClose={handleClose}
      variant="temporary"
      ModalProps={{
        disablePortal: true,
      }}
      slots={{
        transition: Fade,
      }}
      slotProps={{
        paper: {
          sx: StyleSheet.paper(contentTop, aside ? asideWidth : 0),
        },
        transition: {
          timeout: {
            enter: 180,
            exit: 120,
          },
        },
      }}
    >
      {/* One tree with or without the aside, so the content (e.g. a field
          being typed in) is not remounted when the aside comes and goes. */}
      <Stack direction="row" sx={StyleSheet.row}>
        {aside ? <Box sx={StyleSheet.aside(asideWidth)}>{aside}</Box> : null}
        <Box sx={StyleSheet.content}>
          <SidePanelContent
            title={title}
            footer={footer}
            onClose={onClose}
            hideCloseButton={hideCloseButton}
          >
            {children}
          </SidePanelContent>
        </Box>
      </Stack>
    </Drawer>
  );

  if (!shortcutBoundary) {
    return drawer;
  }

  return (
    <SaveShortcutBoundary
      active={open}
      priority="modal"
      strategy={shortcutStrategy}
    >
      {drawer}
    </SaveShortcutBoundary>
  );
}

export default SidePanel;
